import { useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import { SpeciesMonthChart } from "@/components/SpeciesMonthChart";
import { SpeciesYearlyChart } from "@/components/SpeciesYearlyChart";
import { SpeciesRecordsList } from "@/components/SpeciesRecordsList";
import { Badge } from "@/components/ui/badge";
import { useOrgContext } from '@/contexts/OrgContext';
import { PageTopNav } from '@/components/nav/PageTopNav';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { Pencil } from 'lucide-react';

const statusColorMap: Record<string, string> = {
  RESIDENT: 'bg-green-100 text-green-800 hover:bg-green-100',
  ANNUAL: 'bg-blue-100 text-blue-800 hover:bg-blue-100',
  UNCOMMON: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100',
  VAGRANT: 'bg-purple-100 text-purple-800 hover:bg-purple-100',
  SCARCE: 'bg-amber-100 text-amber-800 hover:bg-amber-100',
  RARE: 'bg-red-100 text-red-800 hover:bg-red-100',
};

const statusLabelMap: Record<string, string> = {
  RESIDENT: 'Resident',
  ANNUAL: 'Annual',
  UNCOMMON: 'Uncommon',
  VAGRANT: 'Vagrant',
  SCARCE: 'Scarce',
  RARE: 'Rare',
};

export function OrgSpeciesDetails() {
  const { speciesId, orgId } = useParams<{ speciesId: string, orgId: string }>();
  const { orgRole } = useOrgContext();
  const {
    data: orgSpecies,
    isLoading: isLoadingSpecies,
    error: speciesError,
  } = useQuery({
    queryKey: ["orgSpecies", speciesId],
    queryFn: async () => {
      const res = await fetch(`/api/org/${orgId}/species/${speciesId}`);
      const json = await res.json();
      return json;
    },
    enabled: !!speciesId,
  });


  if (isLoadingSpecies) {
    return <Skeleton className="h-32 w-full" />;
  }
  if (speciesError || !orgSpecies) {
    return <div className="text-red-500">Failed to load species details.</div>;
  }

  return (
    <>
      <div className="max-w-3xl mx-auto py-8">
        <PageTopNav
          pageName={`${orgSpecies?.englishName}`}
          rightItem={orgRole === 'ADMIN' ? (
            <Link to={`/app/org/${orgId}/species/${speciesId}/edit`}>
              <Button>
                <Pencil className="h-4 w-4" />
                <span className="hidden md:block">Edit</span>
              </Button>
            </Link>
          ) : null}
        />

        <div className="mb-6">
          <p className="text-muted-foreground italic mb-1">{orgSpecies.scientificName}</p>
          {orgSpecies.authority && (
            <p className="text-sm text-muted-foreground">{orgSpecies.authority}</p>
          )}
        </div>

        <div className="rounded-lg border p-4 mb-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="text-sm text-muted-foreground">Status</div>
            <Badge className={statusColorMap[orgSpecies.status] || 'bg-gray-100 text-gray-800'}>
              {statusLabelMap[orgSpecies.status] || orgSpecies.status}
            </Badge>
          </div>
          <div className="text-sm">
            <span className="font-semibold">Note: </span>
            {orgSpecies.note || <span className="text-gray-400">No note</span>}
          </div>
        </div>
      </div>
      <div className="flex flex-row flex-wrap gap-4 mb-6">
        <div className="basis-full lg:basis-1/2">
          <SpeciesMonthChart orgId={orgId!} speciesId={speciesId!} />
        </div>
        <div className="basis-full lg:basis-1/2">
          <SpeciesYearlyChart orgId={orgId!} speciesId={speciesId!} />
        </div>
      </div>
      <div className="mt-6">
        <SpeciesRecordsList orgId={orgId!} speciesId={speciesId!} />
      </div>
    </>
  );
}
